import { Component, EventEmitter, Input, OnChanges, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TableConfig } from '@app/interfaces';
import { FieldClaimsEditModel } from '@app/models/system-settings';
import { TableComponent } from '@app/shared/components/table/table.component';

/**
 * Table of field claims for a role. Each row has a checkbox that is checked when the row's `ClaimValue` equals its
 * `FieldName`.
 *
 * @remarks
 * Presentational standalone component; the parent owns the claims. Checking a row copies `FieldName` to `ClaimValue`,
 * unchecking sets `ClaimValue` to null. The claims are edited in place.
 */
@Component({
	selector: 'sb-role-field-claims',
	standalone: true,
	imports: [MatCheckboxModule, TableComponent],
	template: `
		<sl-table [data]="rows" [config]="tableConfig" noDataText="No field claims"></sl-table>

		<ng-template #claimCheckbox let-row="row">
			<mat-checkbox
				color="primary"
				[checked]="isChecked(row)"
				[disabled]="readOnly"
				[attr.aria-label]="'Allow ' + row.Description"
				(change)="setChecked(row, $event.checked)"
			></mat-checkbox>
		</ng-template>
	`,
	styles: [
		`
			mat-checkbox {
				--custom-margin-bottom-mat-checkbox: 0;
				margin: 0;
			}
		`,
	],
})
export class RoleFieldClaimsComponent implements OnChanges {
	@ViewChild('claimCheckbox', { static: true }) private readonly claimCheckbox: TemplateRef<unknown>;

	/** The claims to display. Their `ClaimValue`s are edited in place. */
	@Input({ required: true }) fieldClaims: FieldClaimsEditModel[];

	/** Displays the values without allowing changes. */
	@Input() readOnly = false;

	/** Emits (without a payload) after a `ClaimValue` has changed. */
	@Output() changed = new EventEmitter<void>();

	/** The claims ordered by module, then page, which is the order the table starts in and the tie-break order when sorting. */
	rows: FieldClaimsEditModel[] = [];

	readonly tableConfig: TableConfig<FieldClaimsEditModel> = {
		columns: [
			{ header: 'Module', key: 'ModuleDisplayName' },
			{ header: 'Page', key: 'PageDisplayName' },
			{ header: 'Description', key: 'Description' },
			{ header: '', key: 'ClaimValue', disableSort: true, template: () => this.claimCheckbox },
		],
		defaultSortField: 'ModuleDisplayName',
		defaultSortDirection: 'asc',
		editableRows: false,
		showRowActions: false,
		onRowClick: null,
	};

	/**
	 * Rebuilds the ordered rows whenever `fieldClaims` is replaced.
	 *
	 * @remarks
	 * The table's sort is stable and treats equal values as ties, so ordering the rows by module and then page up front
	 * makes a sort on Module keep the pages in ascending order within each module, in either sort direction.
	 */
	ngOnChanges(): void {
		this.rows = [...(this.fieldClaims ?? [])].sort(
			(a, b) => this.compare(a.ModuleDisplayName, b.ModuleDisplayName) || this.compare(a.PageDisplayName, b.PageDisplayName)
		);
	}

	isChecked(claim: FieldClaimsEditModel): boolean {
		return !!claim.ClaimValue && claim.ClaimValue === claim.FieldName;
	}

	setChecked(claim: FieldClaimsEditModel, checked: boolean): void {
		claim.ClaimValue = checked ? claim.FieldName : null;
		this.changed.emit();
	}

	// Lower-cased to match how the table sorts text.
	private compare(a: string, b: string): number {
		return (a ?? '').toLowerCase().localeCompare((b ?? '').toLowerCase());
	}
}
